import { isEqual } from 'radash';
import { useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';

import { PersistedItem } from '@/types/DataModel';

const isEmptyOrMissing = (value: unknown): boolean =>
    value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);

const isNumericString = (value: unknown): value is string =>
    typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value));

// Inputs store numbers and booleans as strings. Match those to the saved
// JSON types without collapsing two different strings (a GTIN must keep
// its leading zeros).
const valuesMatchPersisted = (currentValue: unknown, originalValue: unknown): boolean => {
    if (isEmptyOrMissing(currentValue) && isEmptyOrMissing(originalValue)) {
        return true;
    }
    if (typeof currentValue === 'number' && isNumericString(originalValue)) {
        return currentValue === Number(originalValue);
    }
    if (typeof originalValue === 'number' && isNumericString(currentValue)) {
        return originalValue === Number(currentValue);
    }
    if (typeof currentValue === 'boolean' && (originalValue === 'true' || originalValue === 'false')) {
        return String(currentValue) === originalValue;
    }
    if (typeof originalValue === 'boolean' && (currentValue === 'true' || currentValue === 'false')) {
        return String(originalValue) === currentValue;
    }
    if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
        return currentValue.length === originalValue.length
            && currentValue.every((entry, index) => valuesMatchPersisted(entry, originalValue[index]));
    }
    if (isPlainObject(currentValue) && isPlainObject(originalValue)) {
        const keys = new Set([...Object.keys(currentValue), ...Object.keys(originalValue)]);
        for (const key of keys) {
            const inCurrent = Object.prototype.hasOwnProperty.call(currentValue, key);
            const inOriginal = Object.prototype.hasOwnProperty.call(originalValue, key);
            // A key present on only one side is a real edit, even when its
            // value is ''. { DE: '' } is not the same as a missing map.
            if (inCurrent !== inOriginal) return false;
            if (!valuesMatchPersisted(currentValue[key], originalValue[key])) return false;
        }
        return true;
    }
    return isEqual(currentValue, originalValue);
};

const dirtyFieldsDifferFromPersisted = (
    dirtyFields: Record<string, unknown>,
    formValues: Record<string, unknown>,
    persistedItem: Record<string, unknown>,
): boolean =>
    Object.keys(dirtyFields).some((key) =>
        !valuesMatchPersisted(formValues[key], persistedItem[key]),
    );

const useHasUnsavedItemChanges = (persistedItem?: PersistedItem) => {
    const { isDirty, dirtyFields } = useFormState();
    const formValues = useWatch();

    return useMemo(() => {
        if (!persistedItem) return isDirty;
        if (!isDirty) return false;
        return dirtyFieldsDifferFromPersisted(
            dirtyFields as Record<string, unknown>,
            formValues as Record<string, unknown>,
            persistedItem as Record<string, unknown>,
        );
    }, [persistedItem, isDirty, dirtyFields, formValues]);
};

const useDirtyFields = () => {
    const { getValues } = useFormContext();
    const { isDirty, dirtyFields } = useFormState();

    const getDirtyFieldsWithValues = () => {
        const allValues = getValues();
        const dirtyFieldsWithValues: any = {};
        for (const field in dirtyFields) {
            if (dirtyFields[field]) {
                dirtyFieldsWithValues[field] = allValues[field];
            }
        }
        return dirtyFieldsWithValues;
    };

    return { isDirty, getDirtyFieldsWithValues };
};

export { dirtyFieldsDifferFromPersisted, useDirtyFields, useHasUnsavedItemChanges };
